'use client';

import React, { useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { QuickCalculatorForm } from '@/components/quick-calculator/QuickCalculatorForm';
import { calculateMoonSign, QuickFactMoonSign, CreateBirthProfileInput } from '@/lib/api/client';
import { Moon } from 'lucide-react';

export interface MoonSignCalculatorClientProps {
  initialResult?: QuickFactMoonSign | null;
  initialError?: string | null;
  namespace?: string;
}

export function MoonSignCalculatorClient({
  initialResult = null,
  initialError = null,
  namespace = 'moonSign'
}: MoonSignCalculatorClientProps) {
  const t = useTranslations(namespace as any);
  const tGlossary = useTranslations('glossary');
  const locale = useLocale();

  const [result, setResult] = useState<QuickFactMoonSign | null>(initialResult);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError);

  const handleCalculate = async (input: CreateBirthProfileInput) => {
    setLoading(true);
    setError(null);
    try {
      const data = await calculateMoonSign(input);
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Failed to calculate Moon Sign');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const getSignName = (signName?: string, sanskritName?: string): string => {
    if (!signName) return '';
    if (locale === 'hi') {
      try {
        const val = tGlossary(`zodiacSigns.${signName}`);
        if (val && !val.startsWith('zodiacSigns.')) return val;
      } catch {}
      return signName;
    }
    return sanskritName ? `${signName} (${sanskritName})` : signName;
  };

  return (
    <div className="space-y-8">
      <QuickCalculatorForm
        title={t('title')}
        subtitle={t('subtitle')}
        onCalculate={handleCalculate}
        loading={loading}
      />

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-xs text-center font-mono">
          {error}
        </div>
      )}

      {result && (
        <div
          className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 sm:p-8 shadow-md space-y-6 text-center sm:text-left"
          data-testid="moon-sign-result"
        >
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="w-20 h-20 rounded-2xl bg-[var(--card-warm)] border border-[var(--line)] flex items-center justify-center text-[var(--gold)]">
              <Moon className="w-10 h-10 text-[var(--gold)]" />
            </div>
            <div>
              <div className="text-[10px] font-bold text-[var(--gold)] uppercase tracking-wider mb-1">
                {t('resultBadge')}
              </div>
              <h2 id="rashi-result-heading" className="text-3xl font-serif font-semibold text-[var(--foreground)]">
                {getSignName(result.sign_name, result.sanskrit_sign_name)}
              </h2>
              <p className="text-xs font-mono text-[var(--gold)] mt-1">
                {t('degreeInSign', {
                  formatted: result.formatted_degree,
                  degrees: result.degree_in_sign.toFixed(2)
                })}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
